import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import {useLocation, useNavigate} from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import TokenService from "../utils/TokenService";
import {useThemeStore} from "../stores/themeStore";

// Three-state auth status. The old code seeded a boolean from mere token
// PRESENCE (`Boolean(getLocalAccessToken())`), so a device with a stale
// (expired) token booted as optimistically "logged in" — rendering authed
// content and hard-redirecting to /calculate, only to be 401'd back to
// /login. That round-trip (a window.location.replace reload) was the white
// flash. We now distinguish "we don't know yet" (PENDING) from a confirmed
// answer, and only ever act on a confirmed status.
export const AUTH_PENDING = 'pending'
export const AUTH_AUTHENTICATED = 'authenticated'
export const AUTH_UNAUTHENTICATED = 'unauthenticated'

const SET_ITEM = 'SET_ITEM'
const originalSetItem = localStorage.setItem;

localStorage.setItem = function(key, value) {
    const event = new Event(SET_ITEM);

    event.value = value;
    event.key = key;

    document.dispatchEvent(event);

    originalSetItem.apply(this, arguments);
};

const REMOVE_ITEM = 'REMOVE_ITEM'
const originalRemoveItem = localStorage.removeItem;

localStorage.removeItem = function(key) {
    const event = new Event(REMOVE_ITEM);

    event.key = key;

    document.dispatchEvent(event);

    originalRemoveItem.apply(this, arguments);
};

// Full-viewport boot loader for the "pending" auth step. Inlined here (not a
// separate component/stylesheet) because it renders in exactly one place — the
// auth gate below — and reuses MUI's CircularProgress, the app's standard
// spinner. The solid, theme-matched background is what keeps the pending step
// from ever appearing as a white flash.
const AuthLoadingScreen = () => {
    const theme = useThemeStore((state) => state.theme)
    const isLight = theme === 'light'

    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isLight ? '#f0f2f6' : '#0d1220',
            }}
        >
            <CircularProgress size={38} sx={{ color: isLight ? '#2563eb' : '#3b82f6' }} />
        </div>
    )
}

export const AuthContext = createContext(undefined)

export const AuthContextProvider = ( {children} ) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [loginResponse, login] = useFetchHook(fetcher.login)
    const [logoutResponse, logout] = useFetchHook(fetcher.logout)

    // Seed PENDING only when a token exists (needs server validation); with
    // no token we already know the answer, so go straight to UNAUTHENTICATED
    // (fresh device → login renders immediately, no validation round-trip).
    const [authStatus, setAuthStatus] = useState(
        TokenService.getLocalAccessToken() ? AUTH_PENDING : AUTH_UNAUTHENTICATED
    )

    // The /accounts/profile body from the boot validation below, kept so
    // UserContext can seed from it instead of refetching the same payload.
    const [bootProfile, setBootProfile] = useState(null)

    const isLoginPage = location.pathname === '/login'
    const isLandingPage = location.pathname === '/'

    // Backward-compatible derived boolean. Consumers (PrivateRoute, HomePage)
    // still read `isAuthenticated`; it is true ONLY on a confirmed session.
    // Because PENDING is gated below (children don't render while pending),
    // those consumers never observe the pre-validation state.
    const isAuthenticated = authStatus === AUTH_AUTHENTICATED

    // Boot validation: resolve a stale-vs-valid token BEFORE rendering authed
    // content or redirecting. Reuses the existing validation path (the same
    // /accounts/profile endpoint UserContext uses) + the axios refresh
    // interceptor — no new endpoint. Success → AUTHENTICATED; a 401 whose
    // refresh also fails → UNAUTHENTICATED. Skipped on the public landing
    // page so a marketing visitor with a stale token isn't forced through
    // validation (and the interceptor's redirect-to-login) on a public page.
    useEffect(function validateTokenOnBoot() {
        if (authStatus !== AUTH_PENDING) return
        if (isLandingPage) return

        if (!TokenService.getLocalAccessToken()) {
            setAuthStatus(AUTH_UNAUTHENTICATED)
            return
        }

        let cancelled = false
        fetcher.getProfile()
            .then((response) => {
                if (cancelled) return
                // Keep the body — this is the only profile call startup needs.
                setBootProfile(response?.data ?? null)
                setAuthStatus(AUTH_AUTHENTICATED)
            })
            .catch(() => {
                // Interceptor already attempted a refresh; if we land here the
                // token could not be revived. removeTokens is idempotent.
                TokenService.removeTokens()
                if (!cancelled) setAuthStatus(AUTH_UNAUTHENTICATED)
            })
        return () => { cancelled = true }
    }, [authStatus, isLandingPage])

    // A user who reaches /login with a CONFIRMED valid session is sent to the
    // app. Client-side navigate (not window.location.replace) so there's no
    // full reload — and gated on AUTHENTICATED, not token presence, so a
    // stale token no longer triggers the redirect-then-kickout flash.
    useEffect(function redirectToCalcPageWhenAuthenticatedEffect() {
        if (isLoginPage && authStatus === AUTH_AUTHENTICATED) {
            navigate("/calculate", { replace: true })
        }
    }, [authStatus, isLoginPage])

    // On successful login, persist the tokens and flip to AUTHENTICATED — then
    // STOP. Navigation is owned solely by redirectToCalcPageWhenAuthenticatedEffect
    // above (isLoginPage && AUTHENTICATED → client-side navigate to /calculate).
    // The old window.location.replace("/calculate") did a full-document reload,
    // and the browser's blank paint during that reload was the post-login white
    // flash. Setting AUTHENTICATED here triggers the redirect effect, so the SPA
    // transitions without ever reloading — no flash, single navigation path.
    useEffect(function loginEffect() {
        if (loginResponse?.data?.status === 200) {
            TokenService.setLocalAccessToken(loginResponse.data.data.access)
            TokenService.setLocalRefreshToken(loginResponse.data.data.refresh)
            // Any cached profile belongs to the session that just ended. The
            // removeItem hook below normally clears it on logout, but that
            // relies on patching localStorage — this path does not.
            setBootProfile(null)
            setAuthStatus(AUTH_AUTHENTICATED)
        }
    }, [loginResponse])

    useEffect(function logoutEffect() {
        if (logoutResponse?.data?.status === 200) {
            TokenService.removeTokens()
        }
    }, [logoutResponse])

    // Keep auth state in sync with localStorage writes (other tabs / the axios
    // interceptor's token refresh + removeTokens). Subscribed once (empty deps)
    // — the handlers set an absolute status rather than reading current state,
    // so they don't need to re-subscribe. Setting the same status is a no-op in
    // React, so redundant events are harmless.
    useEffect(function syncAuthWithLocalStorage() {
        const onSetItem = (e) => {
            if (e.key === 'accessToken' && Boolean(e.value)) {
                setAuthStatus(AUTH_AUTHENTICATED)
            }
        }
        const onRemoveItem = (e) => {
            // Only auth tokens flip auth state — the old `|| 'refreshToken'`
            // was always truthy, so ANY removeItem logged the user out.
            if (e.key === 'accessToken' || e.key === 'refreshToken') {
                // Drop the cached profile with the tokens, so the next session
                // can't seed UserContext with the previous user's.
                setBootProfile(null)
                setAuthStatus(AUTH_UNAUTHENTICATED)
            }
        }
        document.addEventListener(SET_ITEM, onSetItem, false)
        document.addEventListener(REMOVE_ITEM, onRemoveItem, false)
        return () => {
            document.removeEventListener(SET_ITEM, onSetItem, false)
            document.removeEventListener(REMOVE_ITEM, onRemoveItem, false)
        }
    }, [])

    const value = {
        login,
        loginResponse,
        logout,
        authStatus,
        isAuthenticated,
        bootProfile
    }

    // Gate the app while auth is still resolving: render a styled, branded
    // loading screen instead of children so nothing authed (or the login form)
    // paints on an unvalidated token. Excludes the public landing page, which
    // renders regardless of auth. Only once the status is confirmed do the
    // routes underneath render (app when AUTHENTICATED, /login when not).
    //
    // Also covers the brief window where a CONFIRMED-authed user is on /login
    // and the redirect-to-/calculate effect hasn't fired yet — hold the loading
    // screen rather than flash the login form for one frame.
    const isPendingValidation = authStatus === AUTH_PENDING && !isLandingPage
    const isRedirectingAuthedFromLogin = isLoginPage && authStatus === AUTH_AUTHENTICATED

    if (isPendingValidation || isRedirectingAuthedFromLogin) {
        return (
            <AuthContext.Provider value={value}>
                <AuthLoadingScreen />
            </AuthContext.Provider>
        )
    }

    return (
        <AuthContext.Provider value={value}>
            { children }
        </AuthContext.Provider>
    )
}

export const useAuthContext = () => {
    const context = useContext(AuthContext)

    if (context === undefined) {
        throw new Error('useAuthContext must be used within AuthContext')
    }
    return context
}